import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import { paginationSchema } from '../shared/schemes.js';

const CustomMetricSchema = z.object({
  id: z.string(),
  label: z.string(),
  name: z.string(),
  metricType: z.union([z.literal('number'), z.literal('currency')]).nullable(),
  isDateValidationIgnored: z.boolean().optional(),
  lastFile: z.string().optional().nullable(),
  createdDate: z.string().optional(),
  isUsed: z.boolean().optional(),
  fromUser: z.object({
    userId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
  }),
});

const CustomMetricsResponseSchema = z.object({
  data: z
    .object({
      items: z.array(CustomMetricSchema),
      additional: z.object({
        maxCount: z.number(),
        canBeCreated: z.boolean(),
      }),
    })
    .extend(paginationSchema.shape),
});

export const getCustomMetricsQueryOptions = defaultClient.query({
  url: `data-ingestion/custom-metrics/` as const,
  method: 'GET',
  querySchema: z.object({
    search: z.string().prefault(''),
    pageNumber: z.number().prefault(1),
    itemsPerPage: z.number().prefault(10000),
  }),
  responseSchema: CustomMetricsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data.data;
  },
});

export type CustomMetric = z.infer<typeof CustomMetricSchema>;
export type CustomMetricsResponse = z.infer<typeof CustomMetricsResponseSchema>;
export type CustomMetricsQueryResult = CustomMetricsResponse['data'];

const CreateCustomMetricPayloadSchema = z.object({
  label: z.string().trim(),
  metricType: z.union([z.literal('number'), z.literal('currency')]).nullable(),
  isDateValidationIgnored: z.boolean().optional(),
});

export type CreateCustomMetricPayload = z.infer<
  typeof CreateCustomMetricPayloadSchema
>;

const CreateCustomMetricResponseSchema = z.object({
  data: z.object({
    additional: z.object({
      id: z.string(),
    }),
  }),
});

export type CreateCustomMetricResponse = z.infer<
  typeof CreateCustomMetricResponseSchema
>;

export const createCustomMetric = defaultApi.declareEndpoint({
  url: 'data-ingestion/custom-metrics/' as const,
  method: 'POST',
  requestSchema: CreateCustomMetricPayloadSchema,
  responseSchema: CreateCustomMetricResponseSchema,
});

const UpdateCustomMetricPayloadSchema = z.object({
  label: z.string().trim(),
  isDateValidationIgnored: z.boolean().optional(),
});

export type UpdateCustomMetricPayload = z.infer<
  typeof UpdateCustomMetricPayloadSchema
>;

export const updateCustomMetric = defaultApi.declareEndpoint({
  url: 'data-ingestion/custom-metrics/$id/update/' as const,
  method: 'POST',
  requestSchema: UpdateCustomMetricPayloadSchema,
  responseSchema: z.object({}),
});

export const deleteCustomMetric = defaultApi.declareEndpoint({
  url: 'data-ingestion/custom-metrics/$id/' as const,
  method: 'DELETE',
  responseSchema: z.string(),
});
